/**
 * The frame around Clerk's sign-in and sign-up forms.
 *
 * Two halves. On the left, on paper, the one thing the person came to do:
 * a heading, a line, Clerk's form, and whatever is true about what comes
 * next under it. On the right, a ground at full strength with customers'
 * own words laid on it in a card.
 *
 * The words used to sit on the photograph and the form beside them. That
 * put the heading on a narrow, scrimmed picture, which is the hardest place
 * on the site to hold type, and left the form as the second thing on the
 * page. Now nothing is read over the picture, so it needs no scrim, and
 * the form is first.
 *
 * What the page says and where it sends people is unchanged.
 */
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { BotMark } from '@/design-system/bots'
import { aiAgents } from '@/design-system/botmarks'
import { Glass, Ground } from '@/design-system/ground'
import { BrandMark } from '@/design-system/primitives'
import { useAppTheme } from '@/design-system/island'
import { AuthReviews } from '@/components/onboarding/AuthReviews'
import { GATE_PROOF_SOURCE, gateProof } from '@/components/onboarding-v2/gateProof'
import '@/design-system/clerk.css'
import '@/design-system/auth.css'

interface OnboardingAuthShellProps {
  children: ReactNode
  domain?: string
  prefillUrl?: string
  mode: 'sign-up' | 'sign-in'
  /**
   * Sign-up first: the heading says the account is free, and the line under
   * it says what comes next. Applies to plain sign-up only: a claim in
   * flight keeps its own words and panel, and sign-in has nothing to offer.
   * The page passes it only when the next screen is the start page, where
   * both are true.
   */
  deal?: boolean
  /** Omitted where there is nothing to switch to, as on an invitation. */
  switchHref?: string
  /**
   * Overrides, for a page that uses this shape but is not the ordinary way
   * in. The team invitation is the one: same picture, same Clerk card on
   * paper beside it, different thing waiting and different words.
   */
  title?: string
  body?: string
  aside?: ReactNode
  testId?: string
  formTestId?: string
}

/* What is behind the sign-up, for somebody who came here from a scan. Three
   product facts, and the four assistants their site was checked against,
   drawn with the real marks. No window, no bars, no number nobody sourced. */
function WhatIsWaiting({ domain }: { domain?: string }) {
  const items: [string, string][] = [
    ['Priority roadmap', 'See what to improve first'],
    ['Page-level findings', domain ? `Results for ${domain}` : 'Results for your website'],
    ['3 free page setups', 'Add schema and AI-readable files to your highest-priority pages'],
  ]
  return (
    <aside data-testid="onboarding-auth-value" className="ds-auth__waiting">
      <h2 className="ds-h3">The next step is waiting.</h2>
      <dl className="ds-auth__list">
        {items.map(([label, detail]) => (
          <div key={label} className="ds-auth__item">
            <dt>{label}</dt>
            <dd>{detail}</dd>
          </div>
        ))}
      </dl>
      <div className="ds-auth__agents">
        <p className="ds-small ds-faint">Checked against</p>
        <ul>
          {aiAgents.map((agent) => (
            <li key={agent.name}>
              <BotMark name={agent.name} />
              <span>{agent.name}</span>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  )
}

export default function OnboardingAuthShell({
  children,
  domain,
  prefillUrl,
  mode,
  deal = false,
  switchHref,
  title: titleOverride,
  body: bodyOverride,
  aside,
  testId,
  formTestId,
}: OnboardingAuthShellProps) {
  const theme = useAppTheme()
  const isSignUp = mode === 'sign-up'
  const isClaimingReport = Boolean(domain)
  const showValuePanel = isSignUp && isClaimingReport
  /* The deal is for plain sign-up: nobody claiming a report, nobody signing in. */
  const showDeal = deal && isSignUp && !isClaimingReport
  const defaultTitle = isSignUp
    ? isClaimingReport
      ? 'Your scan is ready'
      : showDeal
        ? 'Create your free account'
        : 'Create your SuperSchema account'
    : isClaimingReport
      ? 'Welcome back. Your scan is ready.'
      : 'Welcome back to SuperSchema'
  const defaultBody = isSignUp
    ? isClaimingReport
      ? 'Create your free account to reveal your SuperSchema Score, priority fixes, and included Page Setup.'
      : showDeal
        ? 'Next we ask for your website, then show you what AI is told about it.'
        : 'Start your AI clarity workspace so your website can become easier for AI systems to understand, cite, and recommend.'
    : isClaimingReport
      ? 'Sign in to reveal your SuperSchema Score, priority fixes, and included Page Setup.'
      : 'Sign in to review your Site Scan workspace, monitor AI visibility, and keep your business readable for AI systems.'
  const title = titleOverride ?? defaultTitle
  const body = bodyOverride ?? defaultBody
  const switchCopy = isSignUp ? 'Already have an account?' : 'New to SuperSchema?'
  const switchLabel = isSignUp ? (isClaimingReport ? 'Sign in to claim it' : 'Sign in') : 'Start free'

  return (
    <div
      className="ds ds-auth"
      data-ds-theme={theme}
      data-testid={testId}
      /* The shell draws its own way across to the other form, so Clerk's
         copy of the same sentence is not drawn under it. */
      data-switch={switchHref ? 'true' : undefined}
    >
      <div className="ds-auth__left">
        <header className="ds-auth__top">
          <Link to="/" className="ds-auth__mark" aria-label="SuperSchema">
            <BrandMark className="ds-auth__logo" size={28} />
            <span className="ds-auth__word" aria-hidden="true">
              SuperSchema
            </span>
          </Link>
        </header>

        <main className="ds-auth__main">
          <section data-testid="onboarding-auth-primary" className="ds-auth__say">
            <div className="ds-auth__words">
              {/* The domain used to be a monospace uppercase pill with a
                  magnifying glass in it. It is the thing this page is about,
                  so it goes in a sentence. */}
              {domain && <p className="ds-small ds-muted ds-auth__which">Report for {domain}</p>}
              <h1 className="ds-h2">{title}</h1>
              <p className="ds-body ds-muted ds-auth__lead">{body}</p>
            </div>

            <div data-testid={formTestId ?? 'onboarding-clerk-card'} className="ds-auth__form">
              <div data-testid="onboarding-clerk-wrapper" className="onboarding-clerk-wrapper ds-clerk">
                {children}
              </div>
            </div>

            {switchHref && (
              <p className="ds-body ds-muted ds-auth__switch">
                {switchCopy}{' '}
                <Link to={switchHref} className="ds-link">
                  {switchLabel}
                  <ArrowRight aria-hidden="true" />
                </Link>
              </p>
            )}

            {/* Not on the free sign-up: its line says "Next we ask for your
                website", and a box already holding one says the opposite. */}
            {prefillUrl && !isClaimingReport && !showDeal && (
              <div className="ds-auth__ready">
                <label htmlFor="signup-prefill-url" className="ds-field__label">
                  Website ready for your next Scan
                </label>
                <input
                  id="signup-prefill-url"
                  className="ds-input"
                  readOnly
                  value={prefillUrl}
                  aria-label="Website ready for scan"
                />
              </div>
            )}

            {aside ?? (showValuePanel && <WhatIsWaiting domain={domain} />)}
          </section>
        </main>
      </div>

      {/*
        The picture, and what customers said, on glass.

        No words of ours on the photograph, so no scrim: the picture runs at
        full strength, and the reviews bring their own paper to be read on.
        `ground-swirl-12` because it is teal and glass green, the hues the
        interface does not use, and because the middle of it is the part
        worth seeing: this panel is tall, so the picture is cropped to its
        centre.
      */}
      <aside className="ds-auth__proof">
        <Ground
          photo="/design-system/grounds-1/ground-swirl-12.webp"
          className="ds-auth__pic"
          eager
          over={
            <div className="ds-auth__over">
              <Glass from="bottom-right" second="top-left">
                <AuthReviews source={GATE_PROOF_SOURCE} reviews={gateProof()} />
              </Glass>
            </div>
          }
        />
      </aside>
    </div>
  )
}
